import { Component, Input, OnInit, ViewEncapsulation, ViewChild, EventEmitter, Output } from '@angular/core';
import { OrderService, SettingsService } from '../../shared/services/index';
import { ActivatedRoute } from '@angular/router';
import { Order, IApiTypedResult, ITermByIndustryUsageAndCategory, SplFileStorageResult } from '../../shared/models';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { UploaderComponent, SelectedEventArgs, UploadingEventArgs, ActionCompleteEventArgs } from '@syncfusion/ej2-angular-inputs';
import { EmitType } from '@syncfusion/ej2-base';
import { Media } from '../../shared/models/media.model';
import { catchError } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { Utilities } from '../../shared/services/utilities';
import { FeatureService } from '../../shared/services/feature.service';
import { Observable, of } from 'rxjs';

@Component({
    selector: 'order-admin',
    templateUrl: './order-admin.component.html',
    styleUrls: ['./order-admin.component.scss'],
    encapsulation: ViewEncapsulation.Emulated
})
export class OrderAdminComponent implements OnInit {

    // Local variables to display in component, some are calculated.
    orderRevisionId: number = null;

    orderAdminForm: UntypedFormGroup;

    metaDataXmlFileName: string;

    metaDataXmlFilePath: string;

    customFields: { [key: string]: any } = { "ingestFile":  true};

    ingestFileValue = true;

    @Input() order: Order;

    @Input() orderStatuses: ITermByIndustryUsageAndCategory[];
    private orderStatusesNoDraft: ITermByIndustryUsageAndCategory[];

    @Input() postSaveDataNewOrderRevisionId: number;

    @Input() deliveryDoc: Media;

    // If user clicks "delete" for existing
    // delivery doc
    requestDeletionOfExistingDeliveryDoc: boolean;

    ddwFields: Object = { text: 'termValueDescription', value: 'termValueCode' };

    // Tie it back to the parent
    @Output() onOrderAdminFileUpload = new EventEmitter<string>();
    @Output() onIngestFilechanged = new EventEmitter<boolean>();

    // ------------------------------------------------------------
    // File upload
    // ------------------------------------------------------------

    // Angular object
    @ViewChild('adminFileUpload')
    public adminFileUpload: UploaderComponent;
    // Tells uploader what URL to post files to (gets set programmatically later)
    adminFileUploadAsyncSettings: any = null;
    // Valid extensions to upload.  In this case only zip files.
    adminValidFileExtensionsStr = '.zip';
    adminFileMinSize: number;
    adminFileMaxSize: number;
    dateFormat: any;
    isScsIngestFileFeatureVisible: boolean = false;
    public showIngestFile$: Observable<boolean> = of(false);

    constructor(
        private activatedRoute: ActivatedRoute,
        private orderService: OrderService,
        private fb: UntypedFormBuilder,
        private settingsService: SettingsService,
        private toastr: ToastrService,
        private featureService: FeatureService) {
    }

    ngOnInit() {
        // Extract primary key from query string
        this.orderRevisionId = this.orderService.getNumberOrNull(this.activatedRoute.snapshot.params.orderRevisionId);

        this.requestDeletionOfExistingDeliveryDoc = false;

        this.setFeatureFlag('ssdp-scs-delivery-ingestion');

        this.orderAdminForm = this.fb.group({
            orderStatus: [''],
            confirmedDueDate: [''],
            adminFileUpload: [''],
            ingestFile: [true],
        });

        const settings = this.settingsService.getSettings();
        this.adminFileMinSize = settings.FileMinSize;
        this.adminFileMaxSize = settings.FileMaxSize;
        this.dateFormat = Utilities.getLocaleDateString();
    }

    ngAfterViewInit() {
        setTimeout(() => {
            // Sometimes there's a delay before the order object is available.
            // Give the subcomponent a chance to catch up before finishing the load sequence.
            if (this.order) {
                this.orderAdminForm.patchValue({
                    orderStatus: this.order.orderStatus,
                    confirmedDueDate: this.order.confirmedDueDate,
                });

                // Don't know why, but unless you wrap this in a timeout,
                // when you proceed to the next screen, this field is always
                // marked as dirty, even if no changes.
                setTimeout(() => {
                    this.orderAdminForm.controls.confirmedDueDate.markAsPristine();
                }, 200);

                this.adminFileUploadAsyncSettings = {
                    saveUrl: this.orderService.getFileUploadUrl()
                };

                this.onOrderAdminFileUpload.emit('init');

                // Don't show draft status on this page
                this.orderStatusesNoDraft = this.orderStatuses.filter(x => x.termValueDescription !== this.orderService.ORDER_STATUS_DRAFT)

                // We have to do another call to get spl xml
                this.metaDataXmlFileName = '';
                this.metaDataXmlFilePath = '';
                if (this.order.dataCollectionForm && this.order.dataCollectionForm > 0) {
                    this.orderService.getFileStorageSplXml(this.order.dataCollectionForm)
                        .pipe(catchError(this.orderService.handleOrderObjectError))
                        .subscribe(response => {
                            const responseTyped: IApiTypedResult<SplFileStorageResult> = response as IApiTypedResult<SplFileStorageResult>;
                            if (responseTyped.isSuccess) {
                                if (responseTyped.data) {
                                    this.metaDataXmlFileName = responseTyped.data.fileName;
                                    this.metaDataXmlFilePath = responseTyped.data.filePath;
                                }
                            } else {
                                this.toastr.error('Unexpected error retrieving SPL XML file storage ' + responseTyped.message);
                            }
                        },
                          error => {
                            this.toastr.error(error.message);
                          });
                }
            }
        }, 100);
    }

    public showIngestFile(value: boolean): void {
        this.showIngestFile$ = of(value)
    }


    // ------------------------------------------------------------
    // File upload
    // ------------------------------------------------------------

    // Trigger a browse window
    browseClick() {
        // For order step 2-- there is already another file upload control.
        // Other steps 1 and 3 there is not.
        // So the browse button we want is second on step 2, or first on steps 1, 3.
        let allBrowseButtonWrappers = document.getElementsByClassName('e-file-select-wrap');
        let orderAdminBrowseWrapper = allBrowseButtonWrappers[0];
        if (allBrowseButtonWrappers.length > 1) {
            orderAdminBrowseWrapper = allBrowseButtonWrappers[1];
        }
        orderAdminBrowseWrapper.querySelector('button').click();
        return false;
    }

    public onSelected: EmitType<SelectedEventArgs> = (args: any) => {
        // Mark as dirty so we know user has to hit save button
        this.orderAdminForm.markAsDirty();
        this.orderAdminForm.markAsTouched();
    }

    // Upload file to order, validate settings
    public onUploading: EmitType<UploadingEventArgs> = (args: any) => {
        // We're a child component here, but we need the new orderRevisionId from the parent.

        if (this.order.serviceCategory === this.orderService.SERVICE_CATEGORY_ESG) {
            this.ingestFileValue = false;
        }
        this.orderService.uploadOrderDoc(args,
            this.order.orderId,
            this.postSaveDataNewOrderRevisionId,
            this,
            this.orderAdminForm,
            false,
            this.orderService.UPLOAD_FILE_TYPE_DELIVERY,
            this.ingestFileValue);
    }

    public onSuccess: EmitType<Object> = (args: any) => {
        // Successfully uploaded one file.
    }

    // Called after *ALL* files or uploaded, or after one file has an error
    // (yeah that seems weird, we have to do lots of "if" statements to sort through that)
    public onActionComplete: EmitType<ActionCompleteEventArgs> = (args: any) => {
        let atLeastOneGoodUpload = false;
        if (args && args.fileData) {
            for (const myFileData of args.fileData) {
                if ((myFileData as any).statusCode === "2") {
                    // Got at least one good upload
                    atLeastOneGoodUpload = true;
                    break;
                }
            }
        }

        if (atLeastOneGoodUpload) {
            // Successfully uploaded all files. Finished, navigate to next page.
            this.onOrderAdminFileUpload.emit('success');
        }
    }

    onIngestFileChange(event) {
        this.ingestFileValue = !this.ingestFileValue;
        this.onIngestFilechanged.emit(this.ingestFileValue)
        this.onOrderAdminFileUpload.emit('onIngestFileChange');
        this.orderAdminForm.markAsDirty();
        this.orderAdminForm.markAsTouched();
    }

    deleteExistingDeliveryFile() {
        // Ask parent component to delete delivery doc when we save
        this.requestDeletionOfExistingDeliveryDoc = true;
        this.onOrderAdminFileUpload.emit('deleteExistingDeliveryDoc');
        this.orderAdminForm.markAsDirty();
        this.orderAdminForm.markAsTouched();
    }

    undeleteExistingDeliveryFile() {
        // Ask parent component to "never mind" about deleting the delivery doc
        // Haven't save yet, so the deletion was still pending
        this.requestDeletionOfExistingDeliveryDoc = false;
        this.onOrderAdminFileUpload.emit('deleteExistingDeliveryDoc');
        this.orderAdminForm.markAsDirty();
        this.orderAdminForm.markAsTouched();
    }

    onUploadFailure(event) {
        this.onOrderAdminFileUpload.emit('failure');
    }

    downloadFile(filePath: string, fileName: string) {
        this.orderService.downloadFile(filePath, fileName).subscribe(response => {
            this.orderService.postDownloadFile(response, fileName);
        },
          error => {
            this.toastr.error(error);
          });
    }

    // Add CORS headers - for file uploads
    public addCorsHeaders(args: any) {
        args.currentRequest.setRequestHeader('Access-Control-Allow-Origin', '*');
    }

    setFeatureFlag(name: string) {
        this.featureService.IsFeatureEnabled(name)
        .subscribe(data => {
          if (data) {
            this.isScsIngestFileFeatureVisible = data
          }
          else {
            this.isScsIngestFileFeatureVisible = false;
          }
        }, error => {
          // An error has occured. For safe measure, hide the feature.
          this.isScsIngestFileFeatureVisible = false;
  
          // Log to the console since we do not want to alert the user to a feature they may not have
          console.log(`An error has occured checking if the following feature is enabled: ${name}`)
        });
      }

}
function next(value: boolean): Observable<boolean> {
    throw new Error('Function not implemented.');
}

